import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { auth, org as orgApi, APIError } from '../api/client'
import { useAppContext } from '../context/AppContext'
import { useOrg } from '../hooks/useOrg'
import { AuthLayout } from '../components/layout/AuthLayout'
import { AppBrandingHeader } from '../components/auth/AppBrandingHeader'
import {
  OrgBrandingHeader,
  RegistrationModeNotice,
  PoweredByFooter,
  isEmailDomainAllowed,
} from '../components/OrgBranding'
import { Alert, Button, Input, SocialButton } from '../components/ui'
import type { SocialProvider } from '../components/ui/SocialButton'

type Step = 'email' | 'password' | 'invite_code'
type Mode = 'login' | 'register'

export function LoginPage() {
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('return_to')
  const message = searchParams.get('message')
  const errorParam = searchParams.get('error')
  const inviteCodeParam = searchParams.get('invite')

  const { appInfo } = useAppContext()
  const orgContext = useOrg()

  // State
  const [step, setStep] = useState<Step>('email')
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [inviteCode, setInviteCode] = useState(inviteCodeParam || '')
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(
    errorParam ? getErrorMessage(errorParam) : null
  )
  const [emailInfo, setEmailInfo] = useState<{
    exists: boolean
    has_password: boolean
    providers: string[]
    avatar_url: string | null
  } | null>(null)

  // Get OAuth providers based on org config
  const oauthProviders: SocialProvider[] = orgContext.slug
    ? orgContext.oauth.providers
    : ['google', 'github']

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const result = await auth.checkEmail(email)
      setEmailInfo(result)

      // For new users, check registration restrictions
      if (!result.exists && orgContext.slug) {
        // Check domain restriction
        if (orgContext.registration.mode === 'domain_restricted') {
          if (!isEmailDomainAllowed(email, orgContext)) {
            setError(
              `Registration is restricted to email addresses from: ${orgContext.registration.allowedDomains.join(', ')}`
            )
            setLoading(false)
            return
          }
        }

        // Check invite-only mode
        if (orgContext.registration.mode === 'invite_only' && !inviteCode) {
          setMode('register')
          setStep('invite_code')
          setLoading(false)
          return
        }
      }

      setMode(result.exists ? 'login' : 'register')
      setStep('password')
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message)
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleInviteCodeSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      // Validate invite code with the org
      if (orgContext.slug) {
        const result = await orgApi.validateInviteCode(orgContext.slug, inviteCode)
        if (!result.valid) {
          setError('Invalid or expired invite code')
          setLoading(false)
          return
        }
      }

      // Move to password step
      setStep('password')
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message)
      } else {
        setError('Failed to validate invite code')
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (mode === 'register') {
        // Register then auto-login
        await auth.register({ email, password })
        const response = await auth.login({
          email,
          password,
          remember_me: rememberMe,
        })
        handleLoginSuccess(response)
      } else {
        // Login
        const response = await auth.login({
          email,
          password,
          remember_me: rememberMe,
        })
        handleLoginSuccess(response)
      }
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message)
      } else {
        setError('An unexpected error occurred')
      }
      setLoading(false)
    }
  }

  const handleLoginSuccess = (response: {
    email_verified: boolean
    mfa_required: boolean
    device_verification_required: boolean
    device_id: string | null
  }) => {
    // Store return_to for after verification flows
    if (returnTo) {
      sessionStorage.setItem('u_auth_return_to', returnTo)
    }

    // Check if device verification is needed first
    if (response.device_verification_required) {
      if (response.device_id) {
        sessionStorage.setItem('u_auth_device_id', response.device_id)
      }
      window.location.href = '/device-verify'
      return
    }

    // Check if email verification is needed
    if (!response.email_verified) {
      window.location.href = '/verify-email'
      return
    }

    // Check if MFA is required
    if (response.mfa_required) {
      window.location.href = '/mfa-challenge'
      return
    }

    // Redirect to return_to or default
    const redirectUrl = returnTo || import.meta.env.VITE_ACCOUNT_URL || '/'
    window.location.href = redirectUrl
  }

  const handleBack = () => {
    setStep('email')
    setPassword('')
    setError(null)
    setEmailInfo(null)
  }

  const handleChangeEmail = () => {
    handleBack()
  }

  // Determine title based on step and mode
  const getTitle = () => {
    if (step === 'email') return 'Sign in'
    if (step === 'invite_code') return 'Enter invite code'
    return mode === 'login' ? 'Welcome back' : 'Create your account'
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm mx-auto">
        {/* Org or App branding header */}
        {orgContext.slug && !appInfo ? (
          <OrgBrandingHeader
            defaultTitle={getTitle()}
            subtitle={step === 'email' ? 'Sign in to continue' : undefined}
          />
        ) : (
          <AppBrandingHeader
            appInfo={appInfo}
            defaultTitle={getTitle()}
          />
        )}

        {/* Page title when in OAuth flow */}
        {appInfo && (
          <h1 className="text-2xl font-semibold text-[var(--text-primary)] text-center mb-6">
            {step === 'email'
              ? 'Sign in'
              : step === 'invite_code'
                ? 'Enter invite code'
                : mode === 'login'
                  ? 'Enter password'
                  : 'Create password'}
          </h1>
        )}

        {/* Registration mode notice */}
        {step === 'email' && <RegistrationModeNotice />}

        {/* Messages */}
        {message === 'password_reset' && (
          <Alert variant="success" className="mb-4">
            Password reset successfully. Please sign in.
          </Alert>
        )}
        {message === 'registered' && (
          <Alert variant="success" className="mb-4">
            Account created. Please sign in.
          </Alert>
        )}
        {error && (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        )}

        {step === 'email' ? (
          <>
            {/* Social login - dynamic based on org config */}
            {oauthProviders.length > 0 && (
              <>
                <div className="space-y-3 mb-6">
                  {oauthProviders.map((provider) => (
                    <SocialButton
                      key={provider}
                      provider={provider}
                      returnTo={returnTo || undefined}
                    />
                  ))}
                </div>

                {/* Divider */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[var(--border-subtle)]" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[var(--bg-base)] text-[var(--text-secondary)]">
                      or continue with email
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* Email form */}
            <form onSubmit={handleEmailSubmit} className="space-y-4" noValidate>
              <Input
                label="Email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                autoFocus
              />

              <Button type="submit" fullWidth loading={loading}>
                Continue
              </Button>
            </form>

            {/* Powered by footer */}
            <PoweredByFooter />
          </>
        ) : step === 'invite_code' ? (
          <>
            {/* Invite code form */}
            <div className="mb-6 p-3 bg-[var(--bg-elevated)] rounded-lg">
              <p className="text-sm text-[var(--text-secondary)]">
                Creating account for <span className="font-medium text-[var(--text-primary)]">{email}</span>
              </p>
            </div>

            <form onSubmit={handleInviteCodeSubmit} className="space-y-4" noValidate>
              <Input
                label="Invite Code"
                type="text"
                name="invite_code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Enter your invite code"
                required
                autoFocus
              />

              <Button type="submit" fullWidth loading={loading}>
                Continue
              </Button>
            </form>

            {/* Back button */}
            <button
              type="button"
              onClick={handleBack}
              className="mt-4 w-full text-center text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              &larr; Back
            </button>

            {/* Powered by footer */}
            <PoweredByFooter />
          </>
        ) : (
          <>
            {/* Show email with change option */}
            <div className="mb-6 p-3 bg-[var(--bg-elevated)] rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {emailInfo?.avatar_url ? (
                    <img
                      src={emailInfo.avatar_url}
                      alt=""
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-[var(--bg-elevated-2)] flex items-center justify-center">
                      <span className="text-[var(--text-primary)] font-medium text-sm">
                        {email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {mode === 'login' ? 'Signing in as' : 'Creating account for'}
                    </p>
                    <p className="font-medium text-[var(--text-primary)]">
                      {email}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleChangeEmail}
                  className="text-sm text-[var(--text-primary)] hover:opacity-80 transition-opacity"
                >
                  Change
                </button>
              </div>
            </div>

            {/* Existing user - social login option if available */}
            {mode === 'login' &&
              emailInfo?.providers &&
              emailInfo.providers.length > 0 && (
                <>
                  <div className="space-y-3 mb-6">
                    {emailInfo.providers.includes('google') && (
                      <SocialButton
                        provider="google"
                        returnTo={returnTo || undefined}
                      />
                    )}
                    {emailInfo.providers.includes('github') && (
                      <SocialButton
                        provider="github"
                        returnTo={returnTo || undefined}
                      />
                    )}
                  </div>

                  {emailInfo.has_password && (
                    <div className="relative mb-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-[var(--border-subtle)]" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-[var(--bg-base)] text-[var(--text-secondary)]">
                          or use password
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

            {/* Password form */}
            {(mode === 'register' || emailInfo?.has_password) && (
              <form
                onSubmit={handlePasswordSubmit}
                className="space-y-4"
                noValidate
              >
                <Input
                  label="Password"
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={
                    mode === 'register'
                      ? 'Create a password (8+ characters)'
                      : 'Enter your password'
                  }
                  required
                  autoComplete={
                    mode === 'register' ? 'new-password' : 'current-password'
                  }
                  autoFocus
                />

                {mode === 'login' && (
                  <div className="flex items-center justify-between">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="h-4 w-4 rounded border-[var(--border-default)] bg-[var(--bg-elevated)] text-[var(--text-primary)] focus:ring-[var(--text-primary)] focus:ring-offset-[var(--bg-base)]"
                      />
                      <span className="ml-2 text-sm text-[var(--text-secondary)]">
                        Remember me
                      </span>
                    </label>
                    <a
                      href="/forgot-password"
                      className="text-sm text-[var(--text-primary)] hover:opacity-80 transition-opacity"
                    >
                      Forgot password?
                    </a>
                  </div>
                )}

                <Button type="submit" fullWidth loading={loading}>
                  {mode === 'login' ? 'Sign in' : 'Create account'}
                </Button>
              </form>
            )}

            {/* Back button */}
            <button
              type="button"
              onClick={handleBack}
              className="mt-4 w-full text-center text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              &larr; Back
            </button>

            {/* Powered by footer */}
            <PoweredByFooter />
          </>
        )}
      </div>
    </AuthLayout>
  )
}

function getErrorMessage(error: string): string {
  const messages: Record<string, string> = {
    unknown_provider: 'Unknown authentication provider',
    invalid_state: 'Session expired. Please try again.',
    provider_error: 'Authentication error. Please try again.',
    already_linked: 'This account is already linked to another user.',
    identity_error: 'Unable to authenticate. Please try again.',
  }
  return messages[error] || 'An error occurred'
}
